import { useEffect, useMemo } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { serviceSubtypeParticularities } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type FromToLocation, type UnitsOfMeasurement } from "@services/vessel-call/types";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type WasteDisposalFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  selectedPortCode?: string;
};

const WASTE_DOCUMENT_FIELDS: Array<keyof PortServicesFormData> = [
  "attachmentDocuments",
  "garbageDocument",
  "slopsDocument",
  "sludgeDocument",
];

const WasteDisposalFields = ({
  serviceType,
  langBase,
}: {
  serviceType: string;
  langBase: string;
}) => {
  const { formData, updateFormData, selectedPortCode } =
    useOutletContext<WasteDisposalFieldsOutlet>();

  const { t } = useTranslation();
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);
  const { options: unitsOfMeasurement } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-vessel-call",
  );
  const { options: locations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );

  const unitOfMeasurementOptions = useMemo(
    () =>
      unitsOfMeasurement
        .filter((u) => u.isCargoUnit)
        .map((u) => ({ label: u.unitDescription, value: u.unitDescription }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [unitsOfMeasurement],
  );

  const locationsOptions = useMemo(
    () =>
      locations
        .filter(
          ({ portCode, fromToLocationCode }) =>
            portCode === selectedPortCode || fromToLocationCode === "SEA",
        )
        .map(({ fromToLocationCode, locationName }) => ({
          value: fromToLocationCode,
          label: locationName,
        })),
    [selectedPortCode, locations],
  );

  const subtypeConfig = serviceSubtypeParticularities[serviceType];

  const dropdownFieldValue = subtypeConfig?.dropdownField
    ? (formData.getValues(subtypeConfig.dropdownField) as string)
    : "";

  useEffect(() => {
    if (!subtypeConfig) return;
    // Reset fields when service type changes
    formData.setValue("quantity", "");
    formData.setValue("garbageCategory", "");
    formData.setValue("unitOfMeasurement", "");
    formData.setValue("numberOfSkips", "");
    formData.setValue("typeOfSludge", "");
    WASTE_DOCUMENT_FIELDS.forEach((fieldName) => {
      updateFormData(fieldName, []);
      clearFilesForKey(fieldName);
    });
    if (subtypeConfig.hasLocationField) {
      formData.setValue("serviceLocation", "");
    }
  }, [serviceType, subtypeConfig]);

  if (!subtypeConfig) {
    return null;
  }

  return (
    <FormProvider {...formData}>
      {subtypeConfig.options ? (
        <div className="grid grid-cols-2">
          <FormItem<
            PortServicesFormData,
            (typeof serviceSubtypeParticularities)[typeof serviceType]["dropdownField"]
          >
            name={subtypeConfig.dropdownField}
            type="autocomplete"
            options={subtypeConfig.options}
            label={t(`${langBase}.${subtypeConfig.labelKey}`)}
            testId={subtypeConfig.testId}
            onFieldChange={updateFormData}
            autocompleteValue={{ label: dropdownFieldValue, value: dropdownFieldValue }}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-4 gap-4 items-end">
        <FormItem<PortServicesFormData, "quantity">
          name="quantity"
          type="number"
          label={t(`${langBase}.fields.quantity`)}
          testId="portServices-quantity"
          onFieldChange={updateFormData}
          maxDigits={15}
          canBeginWithZero={false}
        />
        <FormItem<PortServicesFormData, "unitOfMeasurement">
          name="unitOfMeasurement"
          type="autocomplete"
          options={unitOfMeasurementOptions}
          label={t(`${langBase}.fields.unit-of-measurement`)}
          testId="unit-of-measurement"
          onFieldChange={updateFormData}
        />
        <FormItem<PortServicesFormData, "numberOfSkips">
          name="numberOfSkips"
          type="number"
          label={t(`${langBase}.fields.number-of-skips`)}
          testId="portServices-numberOfSkips"
          onFieldChange={updateFormData}
          maxDigits={6}
          decimals={0}
          canBeginWithZero={false}
        />
        {subtypeConfig.hasLocationField && (
          <FormItem<PortServicesFormData, "serviceLocation">
            name="serviceLocation"
            type="autocomplete"
            options={locationsOptions}
            label={t(`${langBase}.fields.service-location`)}
            testId="service-location"
            onFieldChange={updateFormData}
          />
        )}
      </div>
    </FormProvider>
  );
};

export default WasteDisposalFields;
